import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, ModalBody } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FormattedMessage } from 'react-intl';

import { currencyCode } from 'Helpers/Geo/Country';
import store from 'Redux/store';
import AddToCartBtn from 'Components/Button/Cart/AddToCartCardBtn';
import CardViewBtn from 'Components/Button/Card/CardViewBtn';
import SpecialFormattedNumber from 'Components/Price/SpecialFormattedNumber';
import ProductCover from 'Components/Media/Image/ProductCover';
import SiteSlider from 'Components/Slider/SiteSlider';


const DOWNLOAD_BUNDLE_TYPES = ['software-bundle', 'elearning-bundle', 'audio-bundle'];

const HitCardQuickLook = ({ hit, featuredCarousel, clickFunction, place }) => {
  const [modalOpen, setModalOpen] = useState(false);

  const productId = hit?.product_id;

  useEffect(() => {
    if (modalOpen) {
      store.dispatch({ type: 'frontend/hit-card/QUICKLOOK_OPENED', payload: { productId } });
    }
  }, [modalOpen, productId]);

  if (!hit) return null;

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const {
    name,
    slug,
    drm,
    bundle_covers: bundleCovers,
    tiered,
    display_type: displayType,
    game_total: gameTotal,
    dlc_total: dlcTotal,
    presale,
  } = hit;

  let showDLC = false;
  if (dlcTotal && dlcTotal >= 1) {
    showDLC = true;
  }

  const isProductBundle = DOWNLOAD_BUNDLE_TYPES.includes(displayType);
  // A bundle of nothing but DLC says "4 DLC" rather than "0 Games + 4 DLC"
  const isDlcOnly = !gameTotal && showDLC;

  const sliderSettings = {
    lg: {
      slidesToShow: 3,
      slidesToScroll: 3,
      sliderWidth: 720,
    },
    xl: {
      slidesToShow: 3,
      slidesToScroll: 3,
      sliderWidth: 804,
    },
    xxl: {
      slidesToShow: 3,
      slidesToScroll: 3,
      sliderWidth: 804,
    },
  };

  const showBundleCarousel = Array.isArray(bundleCovers)
    && bundleCovers.length > 0 && !bundleCovers.includes(null);

  return (
    <React.Fragment>
      {!featuredCarousel &&
        <div className="quicklook-btn-container">
          <Button color="link" className="quicklook-btn" onClick={toggleModal}>
            <FontAwesomeIcon icon={faEye} />
            <FormattedMessage id="quicklook.button.quicklook" defaultMessage="Quick Look" />
          </Button>
        </div>
      }

      {featuredCarousel &&
        <React.Fragment>
          <div className="CarouselPanel__quickLook__title">
            <FormattedMessage
              id="quicklook.title.included.challenger"
              defaultMessage="{total} titles including:"
              values={{
                total: gameTotal + dlcTotal,
              }}
            />
          </div>
          <div className="CarouselPanel__quickLook__buttonContainer">
            <button className="" onClick={toggleModal}>
              <FormattedMessage id="quicklook.button.quicklooktwo" defaultMessage="Quick Look" />
            </button>
          </div>
        </React.Fragment>
      }

      <Modal isOpen={modalOpen} toggle={toggleModal} className="quicklook-modal">
        <Button
          color="link"
          className="close-button"
          onClick={toggleModal}
        >
          <FontAwesomeIcon icon={faXmark} title="Close modal" />
        </Button>
        <ModalBody>
          {showBundleCarousel &&
            <div className="quicklook-slider-container">
              <div className="slider-main-title">
                {name}
              </div>

              {displayType !== 'book-bundle' && displayType !== 'comic-bundle' &&
                <div className="quicklook-key-points">
                  <div className="left-slash" />
                  <div className="poin-box">
                    {hit.type === 'pick-and-mix' &&
                      <FormattedMessage
                        id="quicklook.total-games.choose"
                        defaultMessage="Choose from "
                      />
                    }
                    {isDlcOnly &&
                      <FormattedMessage
                        id="quicklook.total-dlc-only"
                        defaultMessage="{dlcTotal} DLC"
                        values={{ dlcTotal }}
                      />
                    }
                    {!isDlcOnly && isProductBundle &&
                      <FormattedMessage
                        id="quicklook.total-products"
                        defaultMessage="{gameTotal, plural, one {# Product} other {# Products}}"
                        values={{ gameTotal }}
                      />
                    }
                    {!isDlcOnly && !isProductBundle &&
                      <FormattedMessage
                        id="quicklook.total-games"
                        defaultMessage="{gameTotal, plural, one {# Game} other {# Games}}"
                        values={{ gameTotal }}
                      />
                    }
                    &nbsp;
                    {showDLC && !isDlcOnly &&
                      <FormattedMessage
                        id="quicklook.total-dlc"
                        defaultMessage="+ {dlcTotal} DLC"
                        values={{ dlcTotal }}
                      />
                    }
                  </div>
                  <div className="middle-slash" />
                  <div className="poin-box">
                    {hit.type !== 'pick-and-mix' && isProductBundle &&
                      <FormattedMessage
                        id="quicklook.worth-of-products"
                        defaultMessage="{price} worth of products"
                        values={{
                          price: <SpecialFormattedNumber
                            value={hit.fullPrice[currencyCode]}
                            style="currency"
                          />,
                        }}
                      />
                    }
                    {hit.type !== 'pick-and-mix' && !isProductBundle &&
                      <FormattedMessage
                        id="quicklook.worth-of-games"
                        defaultMessage="{price} worth of games"
                        values={{
                          price: <SpecialFormattedNumber
                            value={hit.fullPrice[currencyCode]}
                            style="currency"
                          />,
                        }}
                      />
                    }
                    {hit.type === 'pick-and-mix' &&
                      <FormattedMessage
                        id="quicklook.pnm-from"
                        defaultMessage="From {price}"
                        values={{
                          price: <SpecialFormattedNumber
                            value={hit.fullPrice[currencyCode]}
                            style="currency"
                          />,
                        }}
                      />
                    }
                  </div>
                  <div className="middle-slash" />
                  <div className="poin-box">
                    <FormattedMessage
                      id="quicklook.redeem"
                      defaultMessage="Redeem on {validDrm} "
                      values={{
                        validDrm: drm[0],
                      }}
                    />
                  </div>
                  <div className="right-slash" />
                </div>
              }

              {displayType === 'comic-bundle' &&
                <div className="quicklook-key-points">
                  <div className="left-slash" />
                  <div className="poin-box">

                    {hit.type === 'pick-and-mix' &&
                      <FormattedMessage
                        id="quicklook.total-games.choose"
                        defaultMessage="Choose from "
                      />
                    }
                    <FormattedMessage
                      id="quicklook.total-comics"
                      defaultMessage="{gameTotal, plural, one {# Comic} other {# Comics}}"
                      values={{ gameTotal }}
                    />
                    &nbsp;
                  </div>
                  <div className="middle-slash" />
                  <div className="poin-box">
                    {hit.type !== 'pick-and-mix' &&
                      <FormattedMessage
                        id="quicklook.worth-of-comics"
                        defaultMessage="{price} worth of Comics"
                        values={{
                          price: <SpecialFormattedNumber
                            value={hit.fullPrice[currencyCode]}
                            style="currency"
                          />,
                        }}
                      />
                    }

                    {hit.type === 'pick-and-mix' &&
                      <FormattedMessage
                        id="quicklook.pnm-from"
                        defaultMessage="From {price}"
                        values={{
                          price: <SpecialFormattedNumber
                            value={hit.fullPrice[currencyCode]}
                            style="currency"
                          />,
                        }}
                      />
                    }
                  </div>
                  <div className="middle-slash" />
                  <div className="poin-box">
                    <FormattedMessage
                      id="quicklook.mulidownload"
                      defaultMessage="Multi-format DRM-Free downloads"
                    />
                  </div>
                  <div className="right-slash" />
                </div>
              }

              {displayType === 'book-bundle' &&
                <div className="quicklook-key-points">
                  <div className="left-slash" />
                  <div className="poin-box">
                    {hit.type === 'pick-and-mix' &&
                      <FormattedMessage
                        id="quicklook.total-games.choose"
                        defaultMessage="Choose from "
                      />
                    }
                    <FormattedMessage
                      id="quicklook.total-books"
                      defaultMessage="{gameTotal, plural, one {# eBook} other {# eBooks}}"
                      values={{ gameTotal }}
                    />
                  </div>
                  <div className="middle-slash" />
                  <div className="poin-box">

                    {hit.type !== 'pick-and-mix' &&
                      <FormattedMessage
                        id="quicklook.worth-of-ebooks"
                        defaultMessage="{price} worth of ebooks"
                        values={{
                          price: <SpecialFormattedNumber
                            value={hit.fullPrice[currencyCode]}
                            style="currency"
                          />,
                        }}
                      />
                    }

                    {hit.type === 'pick-and-mix' &&
                      <FormattedMessage
                        id="quicklook.pnm-from"
                        defaultMessage="From {price}"
                        values={{
                          price: <SpecialFormattedNumber
                            value={hit.fullPrice[currencyCode]}
                            style="currency"
                          />,
                        }}
                      />
                    }
                  </div>
                  <div className="middle-slash" />
                  <div className="poin-box">
                    <FormattedMessage
                      id="quicklook.ebook-delivery"
                      defaultMessage="Multi-format downloads"
                    />
                  </div>
                  <div className="right-slash" />
                </div>
              }

              <div className="quicklook-cover-slider-container">
                <SiteSlider sliderLightTheme sliderClass="" sliderSettings={sliderSettings}>
                  {bundleCovers.map(game =>
                    <ProductCover
                      thumbnail
                      cover={game.cover}
                      alt={`Product cover for ${game.name}`}
                      displayType={game.type}
                      key={game.cover}
                    />,
                  )}
                </SiteSlider>
              </div>

              <div className="buttons-container">
                <CardViewBtn
                  hit={hit}
                  place={place}
                  clickFunction={clickFunction}
                  toggleModal={toggleModal}
                />

                {!tiered && !presale &&
                  <AddToCartBtn quickLook id={productId} modalToggle={toggleModal} slug={slug} />
                }
              </div>
            </div>
          }
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

HitCardQuickLook.propTypes = {
  hit: PropTypes.shape({}),
  featuredCarousel: PropTypes.bool,
  clickFunction: PropTypes.func,
  place: PropTypes.string,
};

HitCardQuickLook.defaultProps = {
  hit: null,
  featuredCarousel: false,
  clickFunction: () => { },
  place: '',
};

export default HitCardQuickLook;
